import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { DeviceMobileIcon } from '@phosphor-icons/react/dist/csr/DeviceMobile';
import { FolderOpenIcon } from '@phosphor-icons/react/dist/csr/FolderOpen';
import { FunnelIcon } from '@phosphor-icons/react/dist/csr/Funnel';
import { MagnifyingGlassIcon } from '@phosphor-icons/react/dist/csr/MagnifyingGlass';
import { PaperPlaneTiltIcon } from '@phosphor-icons/react/dist/csr/PaperPlaneTilt';
import { XCircleIcon } from '@phosphor-icons/react/dist/csr/XCircle';

import { Button } from '../../design-system/primitives/Button/Button';
import { Card } from '../../design-system/composites/Card/Card';
import {
  DataTable,
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import { Pagination } from '../../design-system/composites/Pagination/Pagination';
import { useCursorPages } from '../../design-system/composites/Pagination/useCursorPages';
import { Input } from '../../design-system/primitives/Input/Input';
import { SegmentedControl } from '../../design-system/primitives/SegmentedControl/SegmentedControl';
import { ApiError } from '../../lib/api/client';
import { casesApi, type CaseQuery, type CaseRow } from '../../lib/api/cases';
import { smsApi, type SmsMessageRow, type SmsTemplate } from '../../lib/api/sms';
import { statusLabelKey } from '../cases/caseStatusLabel';
import { SendMessageDialog } from '../cases/SendMessageDialog';
import { DirectSmsPanel } from './DirectSmsPanel';
import styles from './SendSmsScreen.module.css';

/**
 * The standalone Send SMS screen — `/send-sms`, reachable only for a
 * session holding `sms.send` (`App.tsx`'s own `rendersSendSms`, the
 * `sendSms` nav entry that has pointed nowhere since Phase 1).
 *
 * M-08 built sending as a panel INSIDE a case (`CaseMessages.tsx` +
 * `SendMessageDialog.tsx`); this screen is the same capability without
 * having to find the case first — an agent on the phone with a patient who
 * quotes a case number or a mobile searches here directly.
 *
 * ## `casesApi.list` is the ONE search this screen has, and that is deliberate
 *
 * There is no "search patients" endpoint, and this screen must not invent
 * one — it reuses `GET /cases` (`casesApi.list`, `CaseList.tsx`'s own
 * filter shape) with only `case_number`/`mobile`, dropping `CaseList`'s
 * `status` filter (this screen is about FINDING a case to message, not
 * auditing the register). `list` is built on `apiInternals.request`, never
 * `send()` — `cases.ts`'s own docblock explains why `send<CasePage>()` would
 * compile and still lose `meta.next_cursor`; this screen inherits that
 * paginated shape unchanged (cursor, "next page"), the identical
 * `CaseList.tsx` pattern.
 *
 * ## `SendMessageDialog` is reused as-is, not forked
 *
 * The dialog takes a `caseUuid` and nothing about "how the agent got here" —
 * it is exactly as case-scoped as it needs to be and no more, so this
 * screen just points it at whichever row's "Send a message" button was
 * clicked (`dialogCaseUuid`), the identical shape `CaseMessages.tsx` already
 * uses for its own single dialog instance.
 *
 * ## The template catalogue loads once, unconditionally
 *
 * Unlike `CaseMessages.tsx` (which only fetches templates when `canSend` is
 * true, because a read-only session can reach that panel too), this whole
 * SCREEN is already gated on `sms.send` before it mounts at all — there is
 * no session here that can view the search without also being able to
 * send, so the catalogue fetch has no condition to gate.
 *
 * ## Success feedback is `CaseMessages`'s own status pill, not an invented toast
 *
 * `onSent` hands back the created `SmsMessageRow` — this screen keeps it
 * per case (`sentByCase`) and renders the identical queued/sending/sent/
 * failed/rejected pill `CaseMessages.tsx` renders in its own message list
 * (`STATUS_CLASS`, `sms.status.*`), not a new "Message sent!" sentence that
 * would be a second vocabulary for the one state `SmsMessageResource`
 * already names.
 *
 * ## Two targets: a case, or a mobile number with no case
 *
 * Product owner: "not all messages require a case number to send a
 * message. messages can also be sent to a phone number directly" — the
 * legacy `callCentre-sendsms.php` was exactly that (a mobile and a message
 * kind). A `SegmentedControl` chooses between the two; it sits in the head
 * of the showing mode's primary card (the design language: the card holds
 * its own controls, nothing floats on the page ground), at the same spot in
 * both cards so choosing never moves it. "A case" is
 * the default and is everything this docblock describes above, unchanged;
 * "A mobile number" is `DirectSmsPanel` (see its own docblock — which
 * templates it offers, and why).
 *
 * The case search stays MOUNTED-BUT-HIDDEN while the other mode is showing
 * (`CodeScreen.tsx`'s `hidden` rule), so switching back and forth keeps the
 * agent's filters, page and per-row sent pills. `DirectSmsPanel` mounts only
 * when first chosen, so a screen used only for cases never fetches the
 * direct-send log at all.
 */

type SendTarget = 'case' | 'mobile';

interface SendSmsRow extends Row, CaseRow {}

function toRow(node: CaseRow): SendSmsRow {
  return { ...node, id: node.uuid };
}

interface FilterState {
  caseNumber: string;
  mobile: string;
}

const BLANK_FILTERS: FilterState = { caseNumber: '', mobile: '' };

function toQuery(filters: FilterState, cursor?: string, perPage?: number): CaseQuery {
  const query: CaseQuery = {};
  if (filters.caseNumber.trim() !== '') query.case_number = filters.caseNumber.trim();
  if (filters.mobile.trim() !== '') query.mobile = filters.mobile.trim();
  if (cursor !== undefined) query.cursor = cursor;
  if (perPage !== undefined) query.per_page = perPage;
  return query;
}

function hasAppliedFilters(query: CaseQuery): boolean {
  return query.case_number !== undefined || query.mobile !== undefined;
}

/**
 * `CaseMessages.tsx`'s own five-entry mapping, duplicated rather than
 * imported — that module exports no such thing (it is a private constant of
 * a component this screen does not otherwise depend on), and the mapping
 * itself is a closed five-value union that will not drift silently: any
 * change to `SmsMessageStatus` (`sms.ts`) is a compile error in BOTH places
 * at once, not a runtime divergence.
 */
const STATUS_CLASS: Record<SmsMessageRow['status'], string> = {
  queued: 'statusQueued',
  sending: 'statusSending',
  sent: 'statusSent',
  failed: 'statusFailed',
  rejected: 'statusRejected',
};

export function SendSmsScreen() {
  const { t } = useTranslation();

  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly SendSmsRow[]>([]);
  const [reference, setReference] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  // 50 — `CaseIndexRequest::DEFAULT_PER_PAGE` server-side.
  const pages = useCursorPages();
  // Destructured — `pages` is a fresh object every render; see
  // `CaseList.tsx`'s identical note.
  const { observe } = pages;
  const [draftFilters, setDraftFilters] = useState<FilterState>(BLANK_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(BLANK_FILTERS);

  const [templates, setTemplates] = useState<readonly SmsTemplate[]>([]);
  const [dialogCaseUuid, setDialogCaseUuid] = useState<string | null>(null);
  const [sentByCase, setSentByCase] = useState<Readonly<Record<string, SmsMessageRow>>>({});
  const [target, setTarget] = useState<SendTarget>('case');
  // Latches true on first switch — see this module's docblock, last section.
  const [mobileOpened, setMobileOpened] = useState(false);

  const requestSeq = useRef(0);
  const pageRef = useRef<HTMLDivElement>(null);

  // The switch moves into the other mode's card when it is used, so the
  // focus moves with it: a keyboard user who chose a mode is left on the
  // switch, not on the document body.
  const switchUsed = useRef(false);
  useEffect(() => {
    if (!switchUsed.current) return;
    switchUsed.current = false;
    pageRef.current
      ?.querySelector<HTMLInputElement>(`[data-mode="${target}"] input[type="radio"]:checked`)
      ?.focus();
  }, [target]);

  const load = useCallback(async (query: CaseQuery) => {
    const seq = ++requestSeq.current;
    setStatus('loading');
    setReference(null);

    try {
      const page = await casesApi.list(query);
      if (seq !== requestSeq.current) return;
      setRows(page.data.map(toRow));
      setTotal(page.meta.total);
      observe({ next_cursor: page.meta.next_cursor, total: page.meta.total });
      setStatus(page.data.length === 0 ? (hasAppliedFilters(query) ? 'no-results' : 'empty') : 'ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (seq !== requestSeq.current) return;
      setReference(error.requestId ?? null);
      setStatus('error');
    }
  }, [observe]);

  useEffect(() => {
    void load(toQuery(appliedFilters, pages.cursor ?? undefined, pages.perPage));
  }, [load, appliedFilters, pages.cursor, pages.perPage]);

  // Unconditional — see this module's own docblock ("The template catalogue
  // loads once, unconditionally").
  useEffect(() => {
    void smsApi.templates().then(setTemplates);
  }, []);

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    pages.first();
    setAppliedFilters(draftFilters);
  }

  function handleClearFilters(): void {
    setDraftFilters(BLANK_FILTERS);
    pages.first();
    setAppliedFilters(BLANK_FILTERS);
  }

  function retry(): void {
    void load(toQuery(appliedFilters, pages.cursor ?? undefined, pages.perPage));
  }

  function updateDraft(field: keyof FilterState) {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setDraftFilters((current) => ({ ...current, [field]: value }));
    };
  }

  function renderStatus(row: Row): ReactNode {
    return t(statusLabelKey((row as unknown as CaseRow).status));
  }

  function renderActions(row: Row): ReactNode {
    const record = row as unknown as CaseRow;
    const sent = sentByCase[record.uuid];

    return (
      <div className={styles['rowActions']}>
        <Button
          variant="secondary"
          onClick={() => {
            setDialogCaseUuid(record.uuid);
          }}
          icon={<PaperPlaneTiltIcon size={16} />}
        >
          {t('sms.sendTitle')}
        </Button>

        {sent === undefined ? null : (
          <span
            role="status"
            className={[styles['statusPill'], styles[STATUS_CLASS[sent.status]]].join(' ')}
          >
            {t(`sms.status.${sent.status}`)}
          </span>
        )}
      </div>
    );
  }

  const columns: readonly Column[] = [
    { key: 'case_number', label: t('table.caseNumber'), neverMirrors: true },
    { key: 'mobile', label: t('table.mobile'), neverMirrors: true },
    { key: 'status', label: t('table.status'), render: renderStatus },
    { key: 'actions', label: t('table.actions'), render: renderActions },
  ];

  const targets = [
    { value: 'case', label: t('sendSms.targetCase'), icon: <FolderOpenIcon size={16} /> },
    { value: 'mobile', label: t('sendSms.targetMobile'), icon: <DeviceMobileIcon size={16} /> },
  ];

  // ONE switch element, placed in the head of whichever mode's card is
  // showing — the same spot in both, so choosing never moves it.
  const modeSwitch = (
    <SegmentedControl
      label={t('sendSms.target')}
      labelPlacement="inline"
      options={targets}
      value={target}
      onChange={(value) => {
        const next = value as SendTarget;
        switchUsed.current = true;
        setTarget(next);
        if (next === 'mobile') setMobileOpened(true);
      }}
    />
  );

  return (
    <div className={styles['page']} ref={pageRef}>
      {mobileOpened ? (
        <div hidden={target !== 'mobile'} data-mode="mobile">
          <DirectSmsPanel templates={templates} modeSwitch={target === 'mobile' ? modeSwitch : undefined} />
        </div>
      ) : null}

      <div className={styles['section']} hidden={target !== 'case'} data-mode="case">
        <Card
          wash
          icon={<MagnifyingGlassIcon size={16} />}
          title={t('sendSms.listTitle')}
          aside={target === 'case' ? modeSwitch : undefined}
        >
          <form className={styles['filters']} onSubmit={handleFilterSubmit} role="search">
            <Input
              label={t('table.caseNumber')}
              width="fill"
              dir="ltr"
              value={draftFilters.caseNumber}
              onChange={updateDraft('caseNumber')}
            />
            <Input
              label={t('table.mobile')}
              width="fill"
              dir="ltr"
              value={draftFilters.mobile}
              onChange={updateDraft('mobile')}
            />

            <div className={styles['filterActions']}>
              <Button type="submit" icon={<FunnelIcon size={16} />}>
                {t('actions.filter')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleClearFilters}
                icon={<XCircleIcon size={16} />}
              >
                {t('actions.clearFilters')}
              </Button>
            </div>
          </form>
        </Card>

        <DataTable
          caption={t('table.caption')}
          columns={columns}
          rows={rows}
          status={status}
          empty={{ body: t('cases.emptyList') }}
          noResults={{ title: t('cases.noResultsList') }}
          onClearFilters={handleClearFilters}
          error={{ title: t('cases.loadFailedList') }}
        />

        <Pagination pages={pages} total={total} rowsOnPage={rows.length} />

        {status === 'error' ? (
          <div className={styles['recovery']}>
            <Button variant="secondary" onClick={retry}
              icon={<ArrowClockwiseIcon size={16} />}
            >
              {t('actions.retry')}
            </Button>

            {reference === null ? null : (
              <p className={styles['reference']}>
                {t('auth.supportReference')}{' '}
                <bdi dir="ltr" data-testid="sendSmsRequestId">
                  {reference}
                </bdi>
              </p>
            )}
          </div>
        ) : null}

        <SendMessageDialog
          open={dialogCaseUuid !== null}
          caseUuid={dialogCaseUuid ?? ''}
          templates={templates}
          onClose={() => {
            setDialogCaseUuid(null);
          }}
          onSent={(sent) => {
            const uuid = dialogCaseUuid;
            setDialogCaseUuid(null);
            if (uuid === null) return;
            setSentByCase((current) => ({ ...current, [uuid]: sent }));
          }}
        />
      </div>
    </div>
  );
}
